<script setup lang="ts">

import {computed, getCurrentInstance, onMounted, reactive, ref, watch} from "vue";
import {WebFormModel} from "capitalist-shared/src/core/model";
import {RemoteRequestState} from "capitalist-shared/src/core/remote";

import PayoutDateInput from "@/components/forms/payout/forms/PayoutDateInput.vue";
import HorizontalFormGroup from "@/components/forms/horizontal/HorizontalFormGroup.vue";
import MultipleDocumentFilesTableUpload
  from "@/components/forms/files/MultipleDocumentFilesTableUpload.vue";
import AddressVerificationDocuments from "@/views/pages/profile/AddressVerificationDocuments.vue";
import PayoutInputHintBlock from "@/components/forms/payout/PayoutInputHintBlock.vue";
import CapInputSwitchbox from "@/components/forms/cap-input-switchbox.vue";
import ErrorMessageBlock from "@/components/forms/payout/ErrorMessageBlock.vue";
import DropdownKeyvalue from "@/components/forms/dropdown/DropdownKeyValue.vue";
import FilteredInputWrapper from "@/components/forms/payout/forms/inputs/FilteredInputWrapper.vue";
import RegularFormNumberedItemHeading
  from "@/views/pages/profile/RegularFormNumberedItemHeading.vue";
import VueI18n from "vue-i18n";
import moment from "moment-timezone";
import {scrollToFormError} from "@/core/scroll";
import ModelValuesAutoSaver from "@/views/pages/profile/ModelValuesAutoSaver.vue";
import IdDocumentsRequirements from "@/views/pages/profile/IdDocumentsRequirements.vue";
import AddressDocumentsRequirements from "@/views/pages/profile/AddressDocumentsRequirements.vue";
import httpAxiosApi from "capitalist-shared/src/core/axios.errors.handle";

const idTypes = {
  ['DOMESTIC_PASSPORT']: 'Внутренний паспорт',
  ['INTERNATIONAL_PASSPORT']: 'Заграничный паспорт',
  ['DRIVING_LICENSE']: 'Водительское удостоверение',
};

class ProfileUpdateIndividualModel extends WebFormModel {
  loadState = new RemoteRequestState();
  submitState = new RemoteRequestState();

  values = {
    firstName: '',
    lastName: '',
    middleName: '',
    birthDay: '',
    activity: '',
    idType: '',
    phone: '',

    idNumber: '',

    raCountry: '',
    raCity: '',
    raAddressLine: '',
    raZip: '',
    raState: '',
    sameAddress: true,
    paAddressLine: '',
    paCity: '',
    paZip: '',
    paState: '',
    paCountry: '',
    directorIdFiles: [],
    addressDocument: [],
    documentValidTo: '',
    documentValidFrom: ''
  }

  countries = []

  loadModel() {
    this.loadState.loadStart();
    return httpAxiosApi.get('/webapi/profile/update/individualModel')
      .then((res) => {
        this.options = res.data;
        this.countries = [...res.data.countries];
        this.loadState.loadEnd();
      })
      .catch((err) => {
        this.loadState.loadError();
        throw err;
      })
  }

  validate() {
    return true;
    if (this.values.directorIdFiles.length == 0) {
      this.errors.set('documentFile', 'Необходимо загрузить копии документов');
    }

    if (this.values.directorIdFiles.length > 10) {
      this.errors.set('documentFile', 'Максимальное количество файлов: 10');
    }

    if (!Object.keys(idTypes).includes(this.values.idType)) {
      this.errors.set('idType', 'Необходимо выбрать тип документа.');
    }

    return !this.errors.has()
  }

  submit() {
    this.errors.clear();
    if (!this.validate()) return;
    this.submitState.loadStart();
    return httpAxiosApi.post('/webapi/profile/update/individual', {
      m: {...this.values, documentFile: this.values.directorIdFiles.map((item) => item.id)}
    })
      .then((res) => {
        this.submitState.loadEnd();
        return res.data;
      })
      .catch((err) => {
        this.checkAxiosError(err);
        this.submitState.loadError();
        throw err;
      })
  }
}

const model = reactive(new ProfileUpdateIndividualModel());
const emits = defineEmits(['submit', 'check-profile'])

const busy = computed(() => {
  return model.submitState.isLoading || model.loadState.isLoading;
})

const isFormDisabled = computed(() => {
  return model.submitState.isLoading;
})

const submitForm = () => {
  model.submit().then(() => {
    emits('submit');
    dataSaver.value?.clear();
  }).catch((error) => {
    scrollToFormError();

    if (error && (typeof error === 'object')
      && error.response
      && error.response?.status) {
      if (error.response?.status == 422) {
        if (error.response.data?.model.hasActiveRequest || error.response.data?.model.alreadyVerified) {
          emits('check-profile');
        }
      }
    }
  })
}

onMounted(() => {
  model.loadModel();
})

const onAddressDocumentsChanged = (docs) => {
  model.values.addressDocument = [...docs]
}


const addressDocuments = ref(null);
const companyFilesSelector = ref(null);
const directorFilesSelector = ref(null);


const isFileUploading = computed(() => {
  return model.loadState.isLoading
    || addressDocuments.value?.busy
    || companyFilesSelector.value?.busy
    || directorFilesSelector.value?.busy;
})

watch(() => [
  model.values.raCountry,
  model.values.raCity,
  model.values.raAddressLine,
  model.values.raZip,
  model.values.raState,
  model.values.sameAddress
].join(':'), (v) => {
  if (model.values.sameAddress) {
    model.values.paCountry = model.values.raCountry;
    model.values.paCity = model.values.raCity;
    model.values.paAddressLine = model.values.raAddressLine;
    model.values.paZip = model.values.raZip;
    model.values.paState = model.values.raState;
  }
});

const datePickerOptions = computed(() => {
  const i18n = getCurrentInstance()?.proxy.$i18n as VueI18n;

  return {
    range: false,
    dateFormat: 'dd.MM.yyyy',
    minDate: moment().subtract(100, 'years').toDate(),
    maxDate: moment().subtract(18, 'years').toDate(),
    view: 'years',
    //minView: 'months',
    autoClose: true,
    language: i18n ? i18n.locale : 'ru'
    //language: //document.getElementsByTagName("html")[0].getAttribute("lang")
  }
})


const idDocumentDatePickerOptions = computed(() => {
  const i18n = getCurrentInstance()?.proxy.$i18n as VueI18n;

  return {
    range: false,
    dateFormat: 'dd.MM.yyyy',
    minDate: moment().subtract(100, 'years').toDate(),
    maxDate: moment().toDate(),
    view: 'days',
    //minView: 'months',
    autoClose: true,
    language: i18n ? i18n.locale : 'ru'
    //language: //document.getElementsByTagName("html")[0].getAttribute("lang")
  }
})

const idDocumentEndDatePickerOptions = computed(() => {
  const i18n = getCurrentInstance()?.proxy.$i18n as VueI18n;

  return {
    range: false,
    dateFormat: 'dd.MM.yyyy',
    minDate: moment().toDate(),
    maxDate: moment().add(30, 'years').toDate(),
    view: 'days',
    //minView: 'months',
    autoClose: true,
    language: i18n ? i18n.locale : 'ru'
    //language: //document.getElementsByTagName("html")[0].getAttribute("lang")
  }
})

const activityOptions = computed(() => {
  const i18n = getCurrentInstance()?.proxy.$i18n as VueI18n;

  const list = [
    'Веб-мастеринг',
    'SEO продвижение',
    'SMO продвижение',
    'Маркетинг, реклама',
    'Дизайн',
    'Копирайтинг',
    'Программирование',
    'Техническая поддержка',
    'Управление проектами',
    'Бизнес услуги'
  ];

  const result = {};

  list.forEach((item) => {
    result[item] = i18n ? i18n.t(item) : item;
  });

  return result;
});

const idTypeOptions = computed(() => {
  const i18n = getCurrentInstance()?.proxy.$i18n as VueI18n;
  const result = {};
  Object.keys(idTypes).forEach((item) => {
    result[item] = i18n ? i18n.t(idTypes[item]) : idTypes[item];
  });
  return result;
})


const countryOptions = computed(() => {
  const result = {};
  const i18n = getCurrentInstance()?.proxy.$i18n as VueI18n;
  const lang = String(i18n ? i18n.locale : 'ru') == 'ru' ? 'Ru' : 'En';

  if (model.loadState.isLoaded && model.countries.length > 0) {
    model.countries.forEach((country) => {
      result[country.alpha2] = country[`name${lang}`];
    });
  }
  return result;
});

const hideForm = () => {
  model.errors.clear();
}

const dataSaver = ref(null);
const activateForm = () => {
  dataSaver.value?.touch();
}

const onSavedDataLoaded = (data) => {
  // console.log('onSaved', data);
  // model.values.birthDay = '01.01.2020';
  model.values = {...model.values, ...data};
}


const birthDay = ref(null),
  documentValidFrom = ref(null),
  documentValidTo = ref(null)


defineExpose({busy, hideForm, activateForm})
</script>

<template>
  <div>
    <ModelValuesAutoSaver ref="dataSaver"
                          save-key="profile-update-individual-form"
                          @loaded="onSavedDataLoaded"
                          :model="model" />
    <form role="form">
      <div class="forms--form-step">
        <RegularFormNumberedItemHeading>
          {{ $t('Персональные данные') }}
          <template v-slot:number>
            2
          </template>
        </RegularFormNumberedItemHeading>


        <HorizontalFormGroup :label="$t('Фамилия')"
                             :required="true"
                             :has-errors="model.errors.has('lastName')">
          <filtered-input-wrapper
            :disabled="isFormDisabled"
            v-model="model.values.lastName"></filtered-input-wrapper>
          <payout-input-hint-block v-slot:hints
                                   :hint="model.getHint('lastName')"></payout-input-hint-block>
          <error-message-block v-slot:errors attribute="lastName|*"
                               :errors="model.errors"></error-message-block>
        </HorizontalFormGroup>
        <HorizontalFormGroup :label="$t('Имя')"
                             :required="true"
                             :has-errors="model.errors.has('firstName')">
          <filtered-input-wrapper
            :disabled="isFormDisabled"
            v-model="model.values.firstName"></filtered-input-wrapper>
          <payout-input-hint-block v-slot:hints
                                   :hint="model.getHint('firstName')"></payout-input-hint-block>
          <error-message-block v-slot:errors attribute="firstName|*"
                               :errors="model.errors"></error-message-block>
        </HorizontalFormGroup>
        <HorizontalFormGroup :label="$t('Отчество')"
                             :required="false"
                             :has-errors="model.errors.has('middleName')">
          <filtered-input-wrapper
            :disabled="isFormDisabled"
            v-model="model.values.middleName"></filtered-input-wrapper>
          <payout-input-hint-block v-slot:hints
                                   :hint="model.getHint('middleName')"></payout-input-hint-block>
          <error-message-block v-slot:errors attribute="middleName|*"
                               :errors="model.errors"></error-message-block>
        </HorizontalFormGroup>

        <HorizontalFormGroup :label="$t('Дата рождения')"
                             :required="true"
                             :has-errors="model.errors.has('birthDay')">
          <!--          <filtered-input-wrapper v-model="model.values.birthDay"></filtered-input-wrapper>-->
          <!--          <PayoutMaskedInput v-model="model.values.birthDay" mask="DD.DD.DDDD"/>-->
          <payout-date-input v-model="model.values.birthDay"
                             ref="birthDay"
                             :disabled="isFormDisabled"
                             :picker-options="datePickerOptions"
                             :init-value="model.values.birthDay"
                             class="form-control"/>
          <payout-input-hint-block v-slot:hints
                                   :hint="$t('Дата рождения в формате ДД.ММ.ГГГГ')"></payout-input-hint-block>
          <error-message-block v-slot:errors attribute="birthDay|*"
                               :errors="model.errors"></error-message-block>
        </HorizontalFormGroup>


        <HorizontalFormGroup :label="$t('Номер телефона')"
                             :required="true"
                             :has-errors="model.errors.has('phone')">
          <filtered-input-wrapper
            :disabled="isFormDisabled"
            v-model="model.values.phone"></filtered-input-wrapper>
          <payout-input-hint-block v-slot:hints
                                   :hint="$t('Код страны - код города - номер телефона.')"></payout-input-hint-block>
          <error-message-block v-slot:errors attribute="phone|*"
                               :errors="model.errors"></error-message-block>
        </HorizontalFormGroup>

        <HorizontalFormGroup :label="$t('Вид деятельности')"
                             :required="true"
                             :has-errors="model.errors.has('activity')">

          <DropdownKeyvalue :options="activityOptions"
                            :disabled="isFormDisabled"
                            v-model="model.values.activity"
                            :placeholder="$t('Выберите вид деятельности')"/>
          <payout-input-hint-block v-slot:hints
                                   :hint="model.getHint('activity')"></payout-input-hint-block>
          <error-message-block v-slot:errors attribute="activity|*"
                               :errors="model.errors"></error-message-block>
        </HorizontalFormGroup>

        <br/>
        <hr/>
        <HorizontalFormGroup>
          <h3>{{ $t('Удостоверение личности') }}</h3>
        </HorizontalFormGroup>

        <HorizontalFormGroup :label="$t('Тип документа')"
                             :required="true"
                             :has-errors="model.errors.has('idType')">
          <DropdownKeyvalue :options="idTypeOptions"
                            :disabled="isFormDisabled"
                            v-model="model.values.idType"
                            :placeholder="$t('Выберите тип документа')"/>
          <error-message-block v-slot:errors attribute="idType|*"
                               :errors="model.errors"></error-message-block>
        </HorizontalFormGroup>

        <HorizontalFormGroup :label="$t('Номер документа')"
                             :required="true"
                             :has-errors="model.errors.has('idNumber')">
          <filtered-input-wrapper
            :disabled="isFormDisabled"
            v-model="model.values.idNumber"></filtered-input-wrapper>
          <error-message-block v-slot:errors attribute="idNumber|*"
                               :errors="model.errors"></error-message-block>
        </HorizontalFormGroup>

        <HorizontalFormGroup :label="$t('Дата начала действия')"
                             :required="true"
                             :has-errors="model.errors.has('documentValidFrom')">
          <payout-date-input v-model="model.values.documentValidFrom"
                             ref="documentValidFrom"
                             :disabled="isFormDisabled"
                             :picker-options="idDocumentDatePickerOptions"
                             :init-value="model.values.documentValidFrom"
                             class="form-control"/>
          <payout-input-hint-block v-slot:hints
                                   :hint="$t('Дата в формате ДД.ММ.ГГГГ')"></payout-input-hint-block>
          <error-message-block v-slot:errors attribute="documentValidFrom|*"
                               :errors="model.errors"></error-message-block>
        </HorizontalFormGroup>

        <HorizontalFormGroup :label="$t('Дата истечения срока действия')"
                             :required="true"
                             :has-errors="model.errors.has('documentValidTo')">
          <payout-date-input v-model="model.values.documentValidTo"
                             ref="documentValidTo"
                             :disabled="isFormDisabled"
                             :picker-options="idDocumentEndDatePickerOptions"
                             :init-value="model.values.documentValidTo"
                             class="form-control"/>
          <payout-input-hint-block v-slot:hints
                                   :hint="$t('Дата в формате ДД.ММ.ГГГГ')"></payout-input-hint-block>
          <error-message-block v-slot:errors attribute="documentValidTo|*"
                               :errors="model.errors"></error-message-block>
        </HorizontalFormGroup>

        <HorizontalFormGroup :label="$t('Копии документов')" :required="true"
                             :has-errors="model.errors.hasErrorsSplit('directorIdFiles')">

          <MultipleDocumentFilesTableUpload
            accept="application/pdf,image/jpeg,image/png"
            :maxFileSize="10485760"
            file-upload-url="/webapi/profile/update?call=uploadFile"
            v-model="model.values.directorIdFiles"
            :max-files="10"
            ref="directorFilesSelector"
            :disabled="isFormDisabled"></MultipleDocumentFilesTableUpload>
          <payout-input-hint-block v-slot:hints
                                   :hint="model.getHint('directorIdFiles')"></payout-input-hint-block>
          <error-message-block v-slot:errors attribute="directorIdFiles|documentFile|*"
                               :errors="model.errors"></error-message-block>
        </HorizontalFormGroup>

        <IdDocumentsRequirements></IdDocumentsRequirements>

        <br/>
        <hr/>
        <HorizontalFormGroup>
          <h3>{{ $t('Адрес прописки') }}</h3>
        </HorizontalFormGroup>

        <HorizontalFormGroup :label="$t('Адрес')"
                             :required="true"
                             :has-errors="model.errors.has('raAddressLine')">
          <filtered-input-wrapper
            :disabled="isFormDisabled"
            v-model="model.values.raAddressLine"></filtered-input-wrapper>
          <error-message-block v-slot:errors attribute="raAddressLine|*"
                               :errors="model.errors"></error-message-block>
        </HorizontalFormGroup>
        <HorizontalFormGroup :label="$t('Город')"
                             :required="true"
                             :has-errors="model.errors.has('raCity')">
          <filtered-input-wrapper
            :disabled="isFormDisabled"
            v-model="model.values.raCity"></filtered-input-wrapper>
          <error-message-block v-slot:errors attribute="raCity|*"
                               :errors="model.errors"></error-message-block>
        </HorizontalFormGroup>
        <HorizontalFormGroup :label="$t('Область / штат')"
                             :required="false"
                             :has-errors="model.errors.has('raState')">
          <filtered-input-wrapper
            :disabled="isFormDisabled"
            v-model="model.values.raState"></filtered-input-wrapper>
          <error-message-block v-slot:errors attribute="raState|*"
                               :errors="model.errors"></error-message-block>
        </HorizontalFormGroup>
        <HorizontalFormGroup :label="$t('Почтовый индекс')"
                             :required="true"
                             :has-errors="model.errors.has('raZip')">
          <filtered-input-wrapper
            :disabled="isFormDisabled"
            v-model="model.values.raZip"></filtered-input-wrapper>
          <error-message-block v-slot:errors attribute="raZip|*"
                               :errors="model.errors"></error-message-block>
        </HorizontalFormGroup>

        <HorizontalFormGroup :label="$t('Страна')"
                             :required="true"
                             :has-errors="model.errors.has('raCountry')">
          <DropdownKeyvalue :options="countryOptions"
                            :disabled="isFormDisabled"
                            v-model="model.values.raCountry"
                            :placeholder="$t('Выберите страну')"/>
          <error-message-block v-slot:errors attribute="raCountry|*"
                               :errors="model.errors"></error-message-block>
        </HorizontalFormGroup>


        <br/>
        <hr/>
        <HorizontalFormGroup>
          <h3>{{ $t('Адрес проживания') }}</h3>
        </HorizontalFormGroup>

        <HorizontalFormGroup>
          <label class="same-address--label">
            <CapInputSwitchbox
              :disabled="isFormDisabled"
              v-model="model.values.sameAddress"></CapInputSwitchbox>
            <div>{{ $t('Совпадает с адресом регистрации') }}</div>
          </label>
        </HorizontalFormGroup>

        <template v-if="!model.values.sameAddress">
          <HorizontalFormGroup :label="$t('Адрес')"
                               :required="true"
                               :has-errors="model.errors.has('paAddressLine')">
            <filtered-input-wrapper
              :disabled="isFormDisabled"
              v-model="model.values.paAddressLine"></filtered-input-wrapper>
            <error-message-block v-slot:errors attribute="paAddressLine|*"
                                 :errors="model.errors"></error-message-block>
          </HorizontalFormGroup>
          <HorizontalFormGroup :label="$t('Город')"
                               :required="true"
                               :has-errors="model.errors.has('paCity')">
            <filtered-input-wrapper
              :disabled="isFormDisabled"
              v-model="model.values.paCity"></filtered-input-wrapper>
            <error-message-block v-slot:errors attribute="paCity|*"
                                 :errors="model.errors"></error-message-block>
          </HorizontalFormGroup>
          <HorizontalFormGroup :label="$t('Область / штат')"
                               :required="false"
                               :has-errors="model.errors.has('paState')">
            <filtered-input-wrapper
              :disabled="isFormDisabled"
              v-model="model.values.paState"></filtered-input-wrapper>
            <error-message-block v-slot:errors attribute="paState|*"
                                 :errors="model.errors"></error-message-block>
          </HorizontalFormGroup>
          <HorizontalFormGroup :label="$t('Почтовый индекс')"
                               :required="true"
                               :has-errors="model.errors.has('paZip')">
            <filtered-input-wrapper
              :disabled="isFormDisabled"
              v-model="model.values.paZip"></filtered-input-wrapper>
            <error-message-block v-slot:errors attribute="paZip|*"
                                 :errors="model.errors"></error-message-block>
          </HorizontalFormGroup>

          <HorizontalFormGroup :label="$t('Страна')"
                               :required="true"
                               :has-errors="model.errors.has('paCountry')">
            <DropdownKeyvalue :options="countryOptions"
                              :disabled="isFormDisabled"
                              v-model="model.values.paCountry"
                              :placeholder="$t('Выберите страну')"/>
            <error-message-block v-slot:errors attribute="paCountry|*"
                                 :errors="model.errors"></error-message-block>
          </HorizontalFormGroup>
        </template>

        <br/>
        <hr/>
        <HorizontalFormGroup>
          <h3>{{ $t('Документы, подтверждающие адрес проживания') }}</h3>
        </HorizontalFormGroup>

        <AddressVerificationDocuments :disabled="isFormDisabled"
                                      @input="onAddressDocumentsChanged"
                                      :model="model"
                                      ref="addressDocuments"
                                      :max-documents="3"></AddressVerificationDocuments>

        <AddressDocumentsRequirements></AddressDocumentsRequirements>
      </div>
    </form>
    <br/>
    <hr/>
    <div class="application--submit">
      <button type="button" class="btn btn-success"
              @click="submitForm"
              :disabled="isFileUploading || isFormDisabled">{{ $t('Отправить') }}
      </button>
      <div v-if="isFileUploading || isFormDisabled">
        <template v-if="isFileUploading">
          <p>
            {{ $t('Пожалуйста подождите пока идет загрузка файлов.') }}
          </p>
        </template>
        <template v-if="isFormDisabled">
          <p>
            {{ $t('Пожалуйста подождите, идет отправка формы.') }}
          </p>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped lang="less">
.forms--form-step {
  padding: 25px 40px 25px 40px;
}

label.same-address--label {
  font-weight: normal;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  cursor: pointer;
}

.application--submit {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  p {
    padding: 1em;
    margin: 0;
  }
}
</style>


<i18n>
{
  "en": {
    "Информация о компании": "Company information",
    "Платежные системы": "Payment systems",
    "Подтверждающие документы": "Proof documents",
    "Прочее": "Other",
    "Партнерские программы": "Affiliate programs",
    "Сервисы для вебмастеров": "Webmaster services",
    "Сервисы обмена валют": "Currency exchange services",
    "Стартапы": "Startups",
    "Рекламные сети": "Advertising networks",
    "Дата рождения в формате ДД.ММ.ГГГГ": "Birth date in format: DD.MM.YYYY",
    "Внутренний паспорт": "Domestic passport",
    "Загрузите скан-копию внутреннего паспорта": "Upload a scan copy of your internal passport",
    "Заграничный паспорт": "International passport",
    "Водительское удостоверение": "Driver's license",
    "Название компании": "Company name",
    "Страна": "Country",
    "Адрес": "Address",
    "Регистрационный номер": "Registration number",
    "Вид деятельности": "Activity type",
    "Номер счета": "Account number",
    "Выберите вид деятельности": "Select activity type",
    "Выберите страну": "Select country",
    "Название банка": "Bank name",
    "SWIFT": "SWIFT",
    "Адрес банка": "Bank address",
    "Код страны - код города - номер телефона.": "Country code - city code - phone number.",
    "Платежные реквизиты": "Payment details",
    "Копии уставных документов": "Copies of statutory documents",
    "Копии документов": "Copies of documents",
    "please_upload_company_docs_hint": "Please upload copies of statutory documents: Certificate of registration, charter, confirmation of registration with the tax authority.",
    "Информация о директоре": "Director information",
    "Фамилия": "Last name",
    "Имя": "First name",
    "Отчество": "Middle name",
    "Номер телефона": "Phone number",
    "Удостоверение личности": "ID document",
    "Тип документа": "Document type",
    "Номер документа": "Document number",
    "Выберите тип документа": "Select document type",
    "Дата начала действия": "Start date",
    "Дата истечения срока действия": "End date",
    "Адрес прописки": "Registration address",
    "Адрес проживания": "Residential address",
    "Область / штат": "State",
    "Почтовый индекс": "Zip code",
    "Совпадает с адресом регистрации": "Same as registration address",
    "Адрес регистрации": "Registration address",
    "Документы, подтверждающие адрес проживания": "Documents confirming the address of residence",
    "Загрузите скан-копию документа, подтверждающего адрес проживания": "Upload a scan copy of the document confirming the address of residence",
    "Дата в формате ДД.ММ.ГГГГ": "Date in format: DD.MM.YYYY",
    "Город": "City",
    "Отправить": "Submit",
    "Пожалуйста подождите пока идет загрузка файлов.": "Please wait while the files are being uploaded.",
    "Пожалуйста подождите, идет отправка формы.": "Please wait, the form is being sent.",
    "Дата рождения": "Date of birth",
    "Персональные данные": "Personal data",
    "Веб-мастеринг": "Webmastering",
    "SEO продвижение": "SEO promotion",
    "SMO продвижение": "SMO promotion",
    "Маркетинг, реклама": "Marketing, advertising",
    "Дизайн": "Design",
    "Копирайтинг": "Copywriting",
    "Программирование": "Programming",
    "Техническая поддержка": "Technical support",
    "Управление проектами": "Project management",
    "Бизнес услуги": "Business services"
  },
  "ru": {
    "please_upload_company_docs_hint": "Пожалуйста, загрузите копии уставных документов: Свидетельство о регистрации, устав, подтверждение постановки на учет в налоговом органе."
  }
}
</i18n>
